import { EditorSelection, EditorState } from "@codemirror/state";
import { syntaxTree } from "@codemirror/language";

import { ancestorOfNodeWithType, descendantsOfNodeWithType } from "./utils/tree-operations/ancestors.js";
import { getMousedownSelection, selectionIntersects } from "./selection.js";

export const selectDecoratedArgument = (field) =>
    EditorState.transactionFilter.of((transaction) => {
        if (!transaction.selection || !transaction.isUserEvent("select.pointer")) {
            return transaction;
        }

        const tree = syntaxTree(transaction.state);
        let selection = transaction.selection;
        const mousedownSelection = getMousedownSelection(transaction.state);
        const rangeSet = transaction.state.field(field, false)?.decorations;
        let replaced = false;

        for (const [index, range] of selection.ranges.entries()) {
            if (!rangeSet) {
                continue;
            }

            let isAtomicRange = false;
            rangeSet.between(range.anchor, range.anchor, (_from, to) => {
                if (to > range.anchor) {
                    isAtomicRange = true;
                    return false;
                }
                return undefined;
            });

            if (!isAtomicRange) {
                continue;
            }

            const replacementRange =
                selectArgument(tree, range, mousedownSelection, 1) ||
                selectArgument(tree, range, mousedownSelection, -1);
            if (replacementRange) {
                selection = selection.replaceRange(replacementRange, index);
                replaced = true;
            }
        }

        if (replaced) {
            return [transaction, { selection }];
        }

        return transaction;
    });

function selectArgument(tree, range, mousedownSelection, side) {
    const anchor = tree.resolveInner(range.anchor, side);

    const command = ancestorOfNodeWithType(anchor, "$Command");
    if (!command) {
        return undefined;
    }

    if (mousedownSelection && selectionIntersects(mousedownSelection, command)) {
        return undefined;
    }

    const [inner] = descendantsOfNodeWithType(command, "$TextArgument");

    if (!inner) {
        return undefined;
    }

    if (side === 1) {
        if (range.anchor === inner.from + 1 || range.anchor === command.from) {
            if (range.empty) {
                return EditorSelection.cursor(inner.from + 1);
            }
            if (Math.abs(range.head - inner.to) < 2) {
                return EditorSelection.range(inner.from + 1, inner.to - 1);
            }
        }
    } else if (range.anchor === inner.to - 1 || range.anchor === command.to) {
        if (range.empty) {
            return EditorSelection.cursor(inner.to - 1);
        }
        if (Math.abs(range.head - command.from) < 2) {
            return EditorSelection.range(inner.to - 1, inner.from + 1);
        }
    }

    return undefined;
}
